import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) {
                    pages.push(i);
                }
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            // Always show last page
            if (!pages.includes(totalPages)) {
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <nav
            className="flex flex-wrap justify-center items-center gap-1.5 sm:gap-2 mt-12 animate-fade-in"
            aria-label="Pagination navigation"
            role="navigation"
        >
            {/* Previous Button */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-3 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-designer-primary
                            ${currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white/70 backdrop-blur-md border border-red-100/30 shadow-red text-designer-primary hover:bg-gradient-to-r hover:from-designer-primary hover:to-red-600 hover:text-white hover:scale-105 hover:shadow-red-hover'
                    }`}
                aria-label="Previous page"
                aria-disabled={currentPage === 1}
            >
                <FaChevronLeft />
            </button>

            {/* Page Numbers - on phones only the current page shows, the rest appear from sm up */}
            {pageNumbers.map((page, index) => {
                if (page === '...') {
                    return (
                        <span
                            key={`ellipsis-${index}`}
                            className="hidden sm:inline px-2 sm:px-3 py-2 text-gray-400"
                            aria-hidden="true"
                        >
                            ...
                        </span>
                    );
                }

                const isCurrent = currentPage === page;
                return (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`${isCurrent ? '' : 'hidden sm:block '}min-w-[40px] sm:min-w-[44px] px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-medium text-sm sm:text-base transition-all duration-300
                                    focus:outline-none focus:ring-2 focus:ring-designer-primary
                                    ${isCurrent
                                ? 'bg-gradient-to-r from-designer-primary to-red-600 text-white shadow-lg shadow-red-500/25 scale-105'
                                : 'bg-white/70 backdrop-blur-md border border-red-100/30 shadow-red text-designer-dark hover:bg-designer-primary/10 hover:text-designer-primary hover:scale-105 hover:shadow-red-hover'
                            }`}
                        aria-label={`Page ${page}`}
                        aria-current={isCurrent ? 'page' : undefined}
                    >
                        {page}
                    </button>
                );
            })}

            <span className="sm:hidden text-xs text-gray-400 font-medium px-1" aria-hidden="true">
                of {totalPages}
            </span>

            {/* Next Button */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-3 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-designer-primary
                            ${currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white/70 backdrop-blur-md border border-red-100/30 shadow-red text-designer-primary hover:bg-gradient-to-r hover:from-designer-primary hover:to-red-600 hover:text-white hover:scale-105 hover:shadow-red-hover'
                    }`}
                aria-label="Next page"
                aria-disabled={currentPage === totalPages}
            >
                <FaChevronRight />
            </button>
        </nav>
    );
};

export default Pagination;
